import { itemLocApi } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { ItemLocListParamBody } from "@/apis/inventoryv2";
import { CommonListParams } from "@/types/common";
import { InventoryLocationItem } from "@/types/inventoryV2";
import { Button, Dropdown, Flex, Input, notification, Tooltip } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import { ItemType } from "antd/lib/menu/interface";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLocationIdx } from "@/utils/wLocation";
import ListFilterBar from "@/components/ListFilterBar";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import { numFormmater } from "@/pages/Requirement/util";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import ColSetButton, { ColSetOption } from "@/components/ColSetButton";
import { useContext } from "react";
import { DictContext } from "@/layout/context/DictContext";

/**
 * 库位出入库明细（LOC_INV_DTL）
 * 数据源 itemLocList，展示 InventoryLocationItem（库位批次维度）数据
 */
const LocInvDetail = () => {
  const { t } = useTranslation();
  const { locationTypes: locTypeDicts } = useContext(DictContext);
  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [keyword, setKeyword] = useState<string>('');
  const [locationTypes, setLocationTypes] = useState<Array<number> | undefined>(undefined);
  const [visibleCols, setVisibleCols] = useState<ColSetOption[] | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const [list, total, listLoading, { fetchList, clearData }] = useDataList<InventoryLocationItem, [CommonListParams<ItemLocListParamBody>?]>({
    fetchApi: itemLocApi
  });

  useEffect(() => {
    fetch(0);
    return () => {
      // 组件卸载时取消进行中的请求，避免卸载后更新状态
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [])

  const fetch = (offset: number) => {
    // 取消上一次请求，避免快速切换筛选时的竞态
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    offset === 0 && tableRef.current?.resetTablePage();
    const take = tableRef.current?.pageSize || DEFAULT_PAGE_SIZE;
    let proCodes: string[] = [];
    if (keyword) {
      proCodes = keyword.trim().split(' ');
    }
    const params: CommonListParams<ItemLocListParamBody> = {
      body: {
        locationTypes: locationTypes?.length ? locationTypes : undefined,
        products: proCodes?.length ? proCodes : undefined,
      },
      pagination: { offset, take },
    }
    fetchList({ ...params, controller })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        clearData();
        notification.error({ message: t('common.serverError') })
      })
  }

  const handleSearch = () => {
    fetch(0);
  };

  const handleClearFilters = () => {
    setLocationTypes(undefined);
    tableRef?.current?.resetTablePage();
    // 清空后立即查询（state 是异步的，这里显式传 undefined）
    const take = tableRef.current?.pageSize || DEFAULT_PAGE_SIZE;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchList({
      body: undefined,
      pagination: { offset: 0, take },
      controller,
    }).catch((err) => {
      if (err?.name === "AbortError") return;
      clearData();
      notification.error({ message: t('common.serverError') })
    })
  };

  const isColHidden = (key: string) => visibleCols !== null && !visibleCols.some(item => item.key === key);

  const columns: ColumnsType<InventoryLocationItem> = useMemo(() => [
    {
      title: t('warehouse.locationIndex'),
      key: 'location',
      width: 120,
      fixed: 'left',
      hidden: isColHidden('location'),
      render(_, record) {
        const text = record.location ? getLocationIdx(record.location) : '';
        return <SText text={text} />
      }
    },
    {
      title: t('inventory.locationType'),
      key: 'locationType',
      width: 160,
      hidden: isColHidden('locationType'),
      render(_, record) {
        const typeInfo = locTypeDicts.find(info => info.key === record.location?.type);
        return <SText text={typeInfo?.desc ?? ''} />
      }
    },
    {
      title: t('warehouse.batchNo'),
      key: 'batchNo',
      width: 300,
      hidden: isColHidden('batchNo'),
      render: (_, record) => <SText text={record.lotItem?.lot?.lot || ''} />
    },
    {
      title: <SText text={t('product.code')} />,
      key: 'productCode',
      width: 120,
      hidden: isColHidden('productCode'),
      render: (_, record) => <SText text={record.lotItem?.product?.code || ''} />
    },
    {
      title: <SText text={t('product.name')} />,
      key: 'productName',
      width: 150,
      hidden: isColHidden('productName'),
      render: (_, record) => <SText text={record.lotItem?.product?.name || ''} />
    },
    {
      title: <SText text={t('product.brand1')} />,
      key: 'model',
      width: 120,
      hidden: isColHidden('model'),
      render: (_, record) => <SText text={record.lotItem?.product?.model || ''} />
    },
    {
      title: <SText text={t('product.uom')} />,
      key: 'uom',
      width: 80,
      hidden: isColHidden('uom'),
      render: (_, record) => <SText text={record.lotItem?.product?.uom || ''} />
    },
    {
      title: t('warehouse.supplierBatchNo'),
      key: 'supplierBatchNo',
      width: 150,
      hidden: isColHidden('supplierBatchNo'),
      render: (_, record) => <SText text={record.lotItem?.lot?.thdLot || ''} />
    },
    {
      title: <SText text={t('purchase.brand')} />,
      key: 'originalBrand',
      width: 120,
      hidden: isColHidden('originalBrand'),
      render: (_, record) => <SText text={record.lotItem?.ext?.originalBrand || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.mpn')} />,
      key: 'originalCode',
      width: 120,
      hidden: isColHidden('originalCode'),
      render: (_, record) => <SText text={record.lotItem?.ext?.originalCode || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('purchase.dcc')} />,
      key: 'dcc',
      width: 110,
      hidden: isColHidden('dcc'),
      render: (_, record) => <SText text={record.lotItem?.ext?.dcc != null ? String(record.lotItem.ext.dcc) : ''} style={{ width: "100%" }} />
    },
    {
      title: t('order.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 100,
      fixed: 'right',
      hidden: isColHidden('count'),
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preIn'),
      dataIndex: 'preIn',
      key: 'preIn',
      width: 100,
      fixed: 'right',
      hidden: isColHidden('preIn'),
      render: (value) => numFormmater(value || 0)
    },
    {
      title: t('warehouse.preOut'),
      dataIndex: 'preOut',
      key: 'preOut',
      width: 100,
      fixed: 'right',
      hidden: isColHidden('preOut'),
      render: (value) => numFormmater(value || 0)
    },
  ], [t, visibleCols, locTypeDicts]);

  // 可配置列，key 与上方 columns 中各列的 key 一一对应
  const colSetOptions: ColSetOption[] = useMemo(() => [
    { key: 'location', label: t('warehouse.locationIndex') },
    { key: 'locationType', label: t('inventory.locationType') },
    { key: 'batchNo', label: t('warehouse.batchNo') },
    { key: 'supplierBatchNo', label: t('warehouse.supplierBatchNo') },
    { key: 'originalBrand', label: t('purchase.brand') },
    { key: 'originalCode', label: t('purchase.mpn') },
    { key: 'dcc', label: t('purchase.dcc') },
    { key: 'productCode', label: t('product.code') },
    { key: 'productName', label: t('product.name') },
    { key: 'model', label: t('product.brand1') },
    { key: 'uom', label: t('product.uom') },
    { key: 'count', label: t('order.quantity') },
    { key: 'preIn', label: t('warehouse.preIn') },
    { key: 'preOut', label: t('warehouse.preOut') },
  ], [t]);

  const itemDataMapper = (item: InventoryLocationItem) => ({
    [t('warehouse.locationIndex')]: item.location ? getLocationIdx(item.location) : '',
    [t('inventory.locationType')]: locTypeDicts.find(info => info.key === item.location?.type)?.desc ?? '',
    [t('warehouse.batchNo')]: item.lotItem?.lot?.lot ?? '',
    [t('warehouse.supplierBatchNo')]: item.lotItem?.lot?.thdLot ?? '',
    [t('purchase.brand')]: item.lotItem?.ext?.originalBrand ?? '',
    [t('purchase.mpn')]: item.lotItem?.ext?.originalCode ?? '',
    [t('purchase.dcc')]: item.lotItem?.ext?.dcc != null ? String(item.lotItem.ext.dcc) : '',
    [t('product.code')]: item.lotItem?.product?.code ?? '',
    [t('product.name')]: item.lotItem?.product?.name ?? '',
    [t('product.brand1')]: item.lotItem?.product?.model ?? '',
    [t('product.uom')]: item.lotItem?.product?.uom ?? '',
    [t('order.quantity')]: numFormmater(item.count ?? 0),
    [t('warehouse.preIn')]: numFormmater(item.preIn ?? 0),
    [t('warehouse.preOut')]: numFormmater(item.preOut ?? 0),
  });

  const handleExportAll = async (): Promise<InventoryLocationItem[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allData: InventoryLocationItem[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await itemLocApi({
          body: {
            locationTypes: locationTypes?.length ? locationTypes : undefined,
            productIds: keyword ? keyword.trim().split(' ') : undefined,
          },
          pagination: { offset, take: batchSize },
        });

        if (response.body && response.body.length > 0) {
          allData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allData.length / (response.pagination?.size || allData.length)) * 100
            ),
            99
          );
          setExportProgress(progress);
        } else {
          hasMore = false;
        }

        if ((response?.body?.length || 0) < batchSize) {
          hasMore = false;
        }
      }
      setExportProgress(100);
      return allData;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const getExportItems = (): ItemType[] => [
    {
      key: 1,
      label: (
        <ExportExcelButton<InventoryLocationItem>
          list={list}
          columns={colSetOptions}
          defaultSelectedColumns={[
            t('warehouse.locationIndex'),
            t('warehouse.batchNo'),
            t('product.code'),
            t('product.name'),
            t('order.quantity'),
          ]}
          dataMapper={itemDataMapper}
          fileName={`${t('layout.locInvDetail')}.xlsx`}
          type="link"
          style={{ color: "black" }}
        />
      )
    },
    {
      key: 2,
      label: (
        <ExportExcelButton<InventoryLocationItem>
          isAll
          loadData={handleExportAll}
          columns={colSetOptions}
          defaultSelectedColumns={[
            t('warehouse.locationIndex'),
            t('warehouse.batchNo'),
            t('product.code'),
            t('product.name'),
            t('order.quantity'),
          ]}
          dataMapper={itemDataMapper}
          fileName={`${t('layout.locInvDetail')}.xlsx`}
          type="link"
          style={{ color: "black" }}
        />
      )
    }
  ];

  const headerChildren = (
    <Flex justify='space-between' style={{ width: "100%" }} align='center' gap={6}>
      <Flex gap={4} align='center'>
        <ColSetButton
          options={colSetOptions}
          value={visibleCols ?? colSetOptions}
          onOk={setVisibleCols}
        />
        <Dropdown menu={{ items: getExportItems() }}>
          <Button onClick={(e) => e.preventDefault()}>
            {t('common.export')}
          </Button>
        </Dropdown>
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
        >
          <SelectLocationType<Array<number>>
            mode="multiple"
            style={{ minWidth: 240 }}
            value={locationTypes}
            onChange={setLocationTypes}
            allowClear
            maxTagCount={1}
          />
          <Tooltip title={t('warehouse.searchMultiCodePlaceholder')}>
            <Input style={{ width: 260 }} value={keyword} onChange={e => setKeyword(e.target.value)} placeholder={t('warehouse.searchMultiCodePlaceholder')} />
          </Tooltip>
        </ListFilterBar>
      </div>
    </Flex >
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%", marginBottom: 10 }}>
      <STableLayout
        tableLayoutRef={tableRef}
        total={total}
        handleChange={fetch}
        columns={columns as ColumnsType<AnyObject>}
        dataSource={list}
        loading={listLoading}
        scrollX={1600}
        headerChildren={headerChildren}
      />
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  )
};

export default LocInvDetail;
